import StoreConnector from '@/helpers/StoreConnector'
import PurchaseOrderApi from 'api-client/PurchaseOrder'
import DeliveryDatesApi from 'api-client/DeliveryDates.api'
import { EventBus } from '@/plugins/event-bus'
import { saveAs } from 'file-saver'
import moment from 'moment'

const PurchaseOrderService = {
  type: 'purchaseOrder',
  addControlCode(selectedProduct) {
    StoreConnector.dispatch('purchaseOrder/addControlCode', selectedProduct)
  },
  removeControlCode(deselectedProduct) {
    StoreConnector.dispatch('purchaseOrder/removeControlCode', deselectedProduct)
  },
  async getPurchaseOrders(page, ponId, searchQuery) {
    try {
      const result = await PurchaseOrderApi.getPurchaseOrders(page, ponId, searchQuery)
      StoreConnector.dispatch('purchaseOrder/addPurchaseOrders', result)
      return Promise.resolve()
    } catch (e) {
      EventBus.$emit('notification-warning', 'purchaseordersgetfailed')
      // always resolve the promise. Else we get an endless loader
      return Promise.resolve()
    }
  },
  async getPurchaseOrder(purchaseOrderId) {
    try {
      const result = await PurchaseOrderApi.getPurchaseOrder(purchaseOrderId)
      StoreConnector.dispatch('purchaseOrder/setPurchaseOrder', result)
      const configurationCode = result.Details.Car.ConfigurationCode

      var deliveryDates = result.DeliveryDates

      if (!deliveryDates) {
        const dealerSettings = await StoreConnector.getOnce('user/dealerSettings')
        deliveryDates = dealerSettings.showDeliveryDates
          ? await DeliveryDatesApi.GetDeliveryDatesByConfigurationCode(configurationCode, true)
          : null
      }

      StoreConnector.commit('purchaseOrder/setExpectedDeliveryDates', deliveryDates)

      // set controlcodes
      result.Details.ControlCodes.forEach((controlCode) => (controlCode.Id = controlCode.ID))
      await StoreConnector.dispatch('purchaseOrder/setControlCodes', result.Details.ControlCodes)

      // set configuration
      await StoreConnector.dispatch('configuration/getConfiguration', {
        configurationCode,
        deliveryDate: result.Details.DeliveryDate,
      })
      await StoreConnector.dispatch(
        'controlCodes/getPurchaseOrderControlCodesForConfigurationCode',
        {
          configurationCode,
          deliveryDate: result.Details.DeliveryDate,
        }
      )

      return Promise.resolve()
    } catch (e) {
      EventBus.$emit('notification-warning', 'purchaseordersgetfailed')
      return Promise.resolve()
    }
  },
  async initNewPurchaseOrder(configurationCode) {
    try {
      const dealerSettings = await StoreConnector.getOnce('user/dealerSettings')
      const deliveryDates = dealerSettings.showDeliveryDates
        ? await DeliveryDatesApi.GetDeliveryDatesByConfigurationCode(configurationCode, true)
        : null
      const deliveryDate =
        deliveryDates != null
          ? moment.utc(deliveryDates.ExpectedDeliveryDateCustomer).format().replace('Z', '')
          : moment.utc().format()

      //todo by default we want to have delivery dates as of stock car

      const employeeUserName = StoreConnector.getOnce('user/userName')
      const employeeName = StoreConnector.getOnce('user/name')
      const employeeEmail = StoreConnector.getOnce('user/email')
      const employee = {
        PonId: employeeUserName,
        EmployeeName: employeeName,
        EmployeeEmail: employeeEmail,
      }

      StoreConnector.dispatch('purchaseOrder/initNewPurchaseOrder', employee)
      StoreConnector.commit('purchaseOrder/setConfigurationCode', configurationCode)
      StoreConnector.commit('purchaseOrder/setDeliveryDate', deliveryDate)
      StoreConnector.commit('purchaseOrder/setExpectedDeliveryDates', deliveryDates)
      await StoreConnector.dispatch('configuration/getConfiguration', {
        configurationCode,
        deliveryDate: deliveryDate,
      })
      await StoreConnector.dispatch(
        'controlCodes/initPurchaseOrderControlCodesForConfigurationCode',
        {
          configurationCode,
          deliveryDate,
        }
      )
    } catch (e) {
      EventBus.$emit('notification-warning', 'Er is een fout opgetreden...')
    }
  },
  async createPurchaseOrder() {
    try {
      const purchaseOrder = await StoreConnector.getOnce('purchaseOrder/get')
      const result = await PurchaseOrderApi.createPurchaseOrder(purchaseOrder)
      await StoreConnector.dispatch('purchaseOrder/setPurchaseOrder', result)
      return result
    } catch (e) {
      EventBus.$emit('notification-warning', 'Er is een fout opgetreden...')
      return Promise.resolve()
    }
  },
  async updatePurchaseOrder() {
    const purchaseOrder = await StoreConnector.getOnce('purchaseOrder/get')
    const ponId = await StoreConnector.getOnce('user/userName')
    try {
      const result = await PurchaseOrderApi.updatePurchaseOrder(purchaseOrder, ponId)
      await StoreConnector.dispatch('purchaseOrder/setPurchaseOrder', result)
      EventBus.$emit('notification-success', 'Inkoopconfiguratie is opgeslagen...')
    } catch (e) {
      EventBus.$emit('notification-warning', 'Er is een fout opgetreden...')
      return Promise.resolve()
    }
  },
  async requestPurchaseOrder() {
    const purchaseOrder = await StoreConnector.getOnce('purchaseOrder/get')
    const ponId = await StoreConnector.getOnce('user/userName')

    try {
      const response = await PurchaseOrderApi.requestPurchaseOrder(purchaseOrder, ponId)
      EventBus.$emit('notification-success', 'Inkoopconfiguratie verzonden')
      return response
    } catch (error) {
      EventBus.$emit('notification-warning', 'Er is een fout opgetreden')
      return Promise.reject(error)
    }
  },
  async updateDeliveryDate(deliveryDate) {
    const configurationCode = StoreConnector.getOnce('purchaseOrder/configurationCode')
    StoreConnector.commit('purchaseOrder/setDeliveryDate', deliveryDate)

    await StoreConnector.dispatch('configuration/getConfiguration', {
      configurationCode,
      deliveryDate: deliveryDate,
    })

    await StoreConnector.dispatch('controlCodes/getPurchaseOrderControlCodesForConfigurationCode', {
      configurationCode,
      deliveryDate: deliveryDate,
    })
  },
  resetPurchaseOrders() {
    StoreConnector.dispatch('purchaseOrder/resetPurchaseOrders')
  },
  async deletePurchaseOrder(id, ponId) {
    try {
      await PurchaseOrderApi.deletePurchaseOrder(id, ponId)
      return Promise.resolve()
    } catch (e) {
      EventBus.$emit('notification-warning', 'purchaseorderdeletefailed')
      // always resolve the promise. Else we get an endless loader
      return Promise.resolve()
    }
  },
  async downloadKomNumbers(id) {
    const result = await PurchaseOrderApi.downloadKomNumbers(id)
    saveAs(new Blob([result.Data], { type: 'text/csv' }), 'Komnummers.csv')
    return Promise.resolve()
  },
  async getPurchaseOrderProgress(purchaseOrderId, transactionId) {
    try {
      const result = await PurchaseOrderApi.getPurchaseOrderProgress(purchaseOrderId, transactionId)
      return result
    } catch (error) {
      EventBus.$emit('notification-warning', 'Er is een fout opgetreden')
      return Promise.reject(error)
    }
  },
}

export default PurchaseOrderService
